import { useMemo, useState } from 'react';
import * as R from 'remeda';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Serie,
  TimeAndCorrectionsPerField,
} from '@rossum/api-client/annotations';
import {
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@rossum/ui/material';
import { dateFormatByGranularity } from '../../../../lib/timeUtils';
import EmptySeries from '../../components/EmptySeries';
import GraphContainer from '../../components/GraphContainer';
import { GranularityT } from '../Granularity';

type Props = {
  series: Array<Serie>;
  groupBy: GranularityT;
};

const TimeAndCorrectionsPerFieldTable = ({ series, groupBy }: Props) => {
  const intl = useIntl();

  const data = series.map(
    ({ values: { timeAndCorrectionsPerField }, beginDate, endDate }) => {
      const date = dateFormatByGranularity(
        intl.locale,
        groupBy,
        beginDate,
        endDate
      );
      return {
        date,
        label: date,
        dimmed: timeAndCorrectionsPerField.length === 0,
        timeAndCorrectionsPerField,
      };
    }
  );

  const [activeSeriesIndex, setActiveSeriesIndex] = useState(0);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const activeSeries = data[activeSeriesIndex];

  const activeData = useMemo(
    () => activeSeries?.timeAndCorrectionsPerField ?? [],
    [activeSeries]
  );

  const sortedData = useMemo(
    () =>
      R.sortBy(activeData, (row: TimeAndCorrectionsPerField) =>
        sortDirection === 'asc' ? row.correctedRatio : -row.correctedRatio
      ),
    [activeData, sortDirection]
  );

  return (
    <GraphContainer graphName="timeAndCorrectionsTable">
      <Select
        size="small"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        disableUnderline
        variant="standard"
        value={activeSeriesIndex}
        renderValue={index => (
          <Typography variant="caption">{data[index]?.label ?? ''}</Typography>
        )}
        onChange={e => {
          const index = e.target.value;
          if (typeof index === 'number') {
            setActiveSeriesIndex(index);
          }
        }}
      >
        {data.map((option, index) => (
          <MenuItem value={index} key={option.label}>
            <Typography
              variant="caption"
              color={option.dimmed ? 'text.disabled' : 'text.primary'}
            >
              {option.label}
            </Typography>
          </MenuItem>
        ))}
      </Select>

      {activeData.length ? (
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '40%' }}>
                  <FormattedMessage id="components.graphs.timeAndCorrectionsTable.fields" />
                </TableCell>
                <TableCell align="right" sx={{ width: '30%' }}>
                  <TableSortLabel
                    active
                    direction={sortDirection}
                    onClick={() =>
                      setSortDirection(prev =>
                        prev === 'asc' ? 'desc' : 'asc'
                      )
                    }
                  >
                    <FormattedMessage id="components.graphs.timeAndCorrectionsTable.pctCorrected" />
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: '30%' }}>
                  <FormattedMessage id="components.graphs.timeAndCorrectionsTable.avgTime" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map(
                ({ schemaId, label, correctedRatio, timeSpentAvgS }) => (
                  <TableRow key={schemaId}>
                    <TableCell title={schemaId}>{label}</TableCell>
                    <TableCell align="right">
                      {(Math.round(correctedRatio * 100 * 100) / 100).toFixed(
                        2
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {(Math.round(timeSpentAvgS * 100) / 100).toFixed(2)}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptySeries inGraph graphName="timeAndCorrectionsTable" />
      )}
    </GraphContainer>
  );
};

export default TimeAndCorrectionsPerFieldTable;
